import { useCallback, useEffect } from 'react';
import first from 'lodash/first';
import orderBy from 'lodash/orderBy';

import type { OptionType } from '@components/Select';
import { useAuthUserCertificates } from '@store/AuthUser/services/hooks/useAuthUserCertificates';
import { store } from '@store';
import { assertIsDefined } from '@utils/assertIsDefined';

export function useCertificate() {
    const { certificateOptions, signContent } = useAuthUserCertificates();
    const { authUserCertificates } = store;

    useEffect(() => {
        authUserCertificates.setSelectedCertificate(first(authUserCertificates.containerCertificates));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [certificateOptions, authUserCertificates.containerCertificates]);

    function selectCertificate(certificate: OptionType<string>): void {
        authUserCertificates.setSelectedCertificate(
            authUserCertificates.containerCertificates.find(item => item.thumbprint === certificate?.value),
        );
    }

    const signData = useCallback(
        (data: string | ArrayBuffer) => {
            const { selectedCertificate } = authUserCertificates;
            assertIsDefined(selectedCertificate, 'Content is signed only after a certificate is selected');
            return signContent(selectedCertificate.thumbprint, data);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [authUserCertificates.selectedCertificate?.thumbprint],
    );

    function getOptions() {
        return orderBy(
            certificateOptions,
            option => {
                const { authUser } = store;

                const fullName = option.label.trim().toLowerCase();

                const lastNameMatch = fullName.includes(authUser.lastName.toLowerCase()) ? 10 : 1;
                const firstNameMatch = fullName.includes(authUser.firstName.toLowerCase()) ? 1 : 0;
                const patronymicMatch =
                    !!authUser.patronymic && fullName.includes(authUser.patronymic.toLowerCase()) ? 1 : 0;

                return (
                    lastNameMatch + lastNameMatch * firstNameMatch + lastNameMatch * firstNameMatch * patronymicMatch
                );
            },
            'desc',
        );
    }

    return {
        selectCertificate,
        signData,
        certificateOptions: getOptions(),
    };
}
